import { Card, Form, Typography, Button, Statistic, Col, Row, InputNumber, Select, Descriptions, Tag } from 'antd';
import { users } from '../../../../../../../API/TT_ELD/Users/user';
import { useParams } from 'react-router-dom';
import { useMutation, UseMutationResult } from 'react-query';
import { useCompanyData, useSubscription } from '../../../../../../../Hooks/TT_ELD/Companies';
import { getUserFromLocalStorage } from '../../../../../../../Utils/sidebar';
import moment from 'moment';

const { Countdown } = Statistic;
const { Text } = Typography;

type MyObjectType = { [key: string | number]: any };
type params = { readonly id: any };
interface FreeTrialResponse { data: any; error: string }

const CAN_SUBMIT_ROLES = ['senior_support', 'manager', 'sales'];

const FreeTrial = () => {
  const [form] = Form.useForm();
  const { id } = useParams<params>();
  const { data, isFetching, isLoading, refetch }: MyObjectType = useCompanyData(id);
  const currentRole = getUserFromLocalStorage()?.role ?? '';
  const canSubmit = CAN_SUBMIT_ROLES.includes(currentRole);

  const {
    data: subscrInfo,
  } = useSubscription(data?.subscriptionId, data?.id);

  // ── Stripe trialing (legacy) ──────────────────────────────────────────────
  const timestamp1 = subscrInfo?.data?.subscription?.trial_start;
  const timestamp2 = subscrInfo?.data?.subscription?.trial_end;
  const stripeStatus = data?.stripeStatus;

  let stripeEndDate: Date | undefined;
  let stripeDays: number | null = null;
  if (typeof timestamp1 === 'number' && typeof timestamp2 === 'number') {
    const d1 = new Date(timestamp1 * 1000);
    stripeEndDate = new Date(timestamp2 * 1000);
    const timeDiff = Math.abs(stripeEndDate.getTime() - d1.getTime());
    stripeDays = Math.floor(timeDiff / (1000 * 3600 * 24)) || null;
  }

  // ── New free_trial object ─────────────────────────────────────────────────
  const freeTrial = data?.freeTrial;
  const isFreeTrialActive = freeTrial?.status === 'active';
  const freeTrialEndDate = freeTrial?.endDate ? new Date(freeTrial.endDate) : undefined;

  const isActivated = !!freeTrial; // trial already created — lock dates

  const onPost: UseMutationResult<
    FreeTrialResponse,
    unknown,
    { values: string | number; id: string | number }
  > = useMutation('freeTrial', ({ values, id }) => users.freeTrial(values, id), {
    onSuccess: (res: any) => {
      refetch();
      if (res.message) form.resetFields();
    },
  });

  const onFinish = async (values: any) => {
    await onPost.mutate({ values, id });
  };

  type FieldType = { limit?: number; quantity?: string };

  const initialQuantity = freeTrial?.quantity ?? data?.quantity;

  return (
    <div className="mainPage">
      <Card loading={isLoading || isFetching} title="Free trial" style={{ width: '100%' }}>

        {/* ── Countdown: Stripe trialing ── */}
        {stripeStatus === 'trialing' && stripeEndDate && (
          <Col style={{ marginBottom: 32 }}>
            <Countdown
              valueStyle={{ fontSize: '32px' }}
              title="Between dates ends"
              value={stripeEndDate.getTime()}
              format=" DD : HH : mm : ss "
            />
            <Text type="secondary" style={{ margin: '0 22px 0 6px' }}>Days</Text>
            <Text type="secondary" style={{ margin: '0 10px 0 2px' }}>Hours</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Minutes</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Seconds</Text>
          </Col>
        )}

        {/* ── Countdown: free_trial object ── */}
        {isFreeTrialActive && freeTrialEndDate && (
          <Col style={{ marginBottom: 32 }}>
            <Countdown
              valueStyle={{ fontSize: '32px' }}
              title="Free trial ends"
              value={freeTrialEndDate.getTime()}
              format=" DD : HH : mm : ss "
            />
            <Text type="secondary" style={{ margin: '0 22px 0 6px' }}>Days</Text>
            <Text type="secondary" style={{ margin: '0 10px 0 2px' }}>Hours</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Minutes</Text>
            <Text type="secondary" style={{ margin: '0 5px 0 5px' }}>Seconds</Text>
          </Col>
        )}

        {/* ── Free trial info ── */}
        {freeTrial && (
          <Descriptions
            bordered
            size="small"
            column={2}
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label="Status">
              <Tag color={freeTrial.status === 'active' ? 'green' : 'default'}>
                {freeTrial.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Outcome">
              <Tag color={
                freeTrial.outcome === 'converted' ? 'blue'
                : freeTrial.outcome === 'cancelled' ? 'red'
                : 'orange'
              }>
                {freeTrial.outcome ?? '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Days">{freeTrial.days ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{freeTrial.quantity ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {freeTrial.startDate ? moment(freeTrial.startDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {freeTrial.endDate ? moment(freeTrial.endDate).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* ── Form ── */}
        <Form
          form={form}
          layout="vertical"
          style={{ maxWidth: 600 }}
          onFinish={onFinish}
          name="basic"
          initialValues={{
            ...data,
            limit: stripeDays ? Number(stripeDays) : 7,
            quantity: initialQuantity,
          }}
        >
          {/* Limit: only show when trial not yet activated (no freeTrial object) and no Stripe sub */}
          {!isActivated && !data?.subscriptionId && (
            <Form.Item<FieldType>
              label="Limit"
              name="limit"
              rules={[{ required: true, message: 'Please input your limit!' }]}
            >
              <Select style={{ width: '100%' }}>
                {Array.from({ length: 10 }, (_, i) => (
                  <Select.Option key={i + 1} value={i + 1}>
                    {i + 1} day{i + 1 > 1 ? 's' : ''}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item<FieldType>
            label="Quantity"
            name="quantity"
            rules={[{ required: true, message: 'Please input your quantity!' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          {canSubmit && (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={onPost.isLoading}>
                Submit
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default FreeTrial;
